import { useRef, useState, useCallback, useEffect } from 'react';
import type { VideoOverlay, VideoAspectRatio, VideoAnimation } from '../types';

interface VideoEditorCanvasProps {
  videoUrl: string;
  aspectRatio: VideoAspectRatio;
  overlays: VideoOverlay[];
  selectedId: string | null;
  onSelectOverlay: (id: string | null) => void;
  onUpdateOverlay: (id: string, updates: Partial<VideoOverlay>) => void;
  onInteractionStart: () => void;
  videoRef: React.RefObject<HTMLVideoElement>;
  currentTime: number;
}

const ASPECT_RATIO_MAP: Record<VideoAspectRatio, string> = {
  '1:1': '1 / 1',
  '9:16': '9 / 16',
  '16:9': '16 / 9',
};

function getAnimationStyle(
  animation: VideoAnimation,
  startTime: number,
  currentTime: number,
  duration: number,
): React.CSSProperties {
  const elapsed = currentTime - startTime;
  const isActive = elapsed >= 0 && elapsed <= duration;
  const isBeforeStart = elapsed < 0;

  if (isBeforeStart) return { opacity: 0, pointerEvents: 'none' };
  if (!isActive) return { opacity: 0, pointerEvents: 'none' };

  const progress = Math.min(elapsed / Math.min(0.5, duration * 0.3), 1);

  switch (animation) {
    case 'fade-in':
      return { opacity: progress, transition: 'opacity 0.3s ease' };
    case 'slide-up':
      return {
        opacity: progress,
        transform: `translateY(${(1 - progress) * 20}px)`,
        transition: 'opacity 0.3s ease, transform 0.3s ease',
      };
    case 'slide-left':
      return {
        opacity: progress,
        transform: `translateX(${(1 - progress) * -30}px)`,
        transition: 'opacity 0.3s ease, transform 0.3s ease',
      };
    case 'pop-in':
      return {
        opacity: progress,
        transform: `scale(${0.7 + progress * 0.3})`,
        transition: 'opacity 0.3s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      };
    case 'none':
    default:
      return { opacity: 1 };
  }
}

export default function VideoEditorCanvas({
  videoUrl,
  aspectRatio,
  overlays,
  selectedId,
  onSelectOverlay,
  onUpdateOverlay,
  onInteractionStart,
  videoRef,
  currentTime,
}: VideoEditorCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<{ id: string; startX: number; startY: number; origX: number; origY: number } | null>(null);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.target === canvasRef.current || (e.target as HTMLElement).closest('[data-canvas-bg]')) {
      onSelectOverlay(null);
    }
  }, [onSelectOverlay]);

  const handleOverlayMouseDown = useCallback((e: React.MouseEvent, overlay: VideoOverlay) => {
    e.stopPropagation();
    e.preventDefault();
    onSelectOverlay(overlay.id);
    onInteractionStart();

    const startX = e.clientX;
    const startY = e.clientY;
    const origX = overlay.x;
    const origY = overlay.y;

    setDragging({ id: overlay.id, startX, startY, origX, origY });
  }, [onSelectOverlay, onInteractionStart]);

  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const dx = ((e.clientX - dragging.startX) / rect.width) * 100;
      const dy = ((e.clientY - dragging.startY) / rect.height) * 100;
      const newX = Math.max(0, Math.min(95, dragging.origX + dx));
      const newY = Math.max(0, Math.min(95, dragging.origY + dy));
      onUpdateOverlay(dragging.id, { x: newX, y: newY });
    };

    const handleMouseUp = () => setDragging(null);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, onUpdateOverlay]);

  return (
    <div className="flex items-center justify-center w-full h-full p-4">
      <div
        className="relative bg-black overflow-hidden"
        style={{
          aspectRatio: ASPECT_RATIO_MAP[aspectRatio],
          maxHeight: '100%',
          maxWidth: '100%',
          height: aspectRatio === '9:16' ? '100%' : 'auto',
          width: aspectRatio === '16:9' ? '100%' : 'auto',
        }}
        ref={canvasRef}
        onClick={handleCanvasClick}
        data-canvas-bg="true"
      >
        {/* Video */}
        <video
          ref={videoRef}
          src={videoUrl}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          playsInline
          muted={false}
        />

        {/* Overlays */}
        {overlays.map((overlay) => {
          const isSelected = selectedId === overlay.id;
          const animStyle = getAnimationStyle(overlay.animation, overlay.startTime, currentTime, overlay.duration);

          return (
            <div
              key={overlay.id}
              className={`absolute cursor-move select-none ${isSelected ? 'ring-2 ring-orange-400 ring-offset-1' : ''}`}
              style={{
                left: `${overlay.x}%`,
                top: `${overlay.y}%`,
                width: `${overlay.width}%`,
                zIndex: isSelected ? 20 : 10,
                ...animStyle,
              }}
              onMouseDown={(e) => handleOverlayMouseDown(e, overlay)}
            >
              {overlay.type === 'logo' && overlay.imageUrl ? (
                <img
                  src={overlay.imageUrl}
                  alt="Logo"
                  className="w-full h-auto object-contain"
                  style={{ opacity: overlay.opacity, borderRadius: overlay.borderRadius }}
                  draggable={false}
                />
              ) : overlay.type === 'cta' ? (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 20px',
                    backgroundColor: overlay.backgroundColor,
                    borderRadius: overlay.borderRadius,
                    color: overlay.color,
                    fontSize: overlay.fontSize,
                    fontFamily: overlay.fontFamily,
                    fontWeight: overlay.fontWeight,
                    opacity: overlay.opacity,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {overlay.content}
                  {overlay.icon && <i className={overlay.icon} style={{ fontSize: overlay.fontSize * 0.9 }} />}
                </div>
              ) : (
                <div
                  style={{
                    color: overlay.color,
                    fontSize: overlay.fontSize,
                    fontFamily: overlay.fontFamily,
                    fontWeight: overlay.fontWeight,
                    opacity: overlay.opacity,
                    backgroundColor: overlay.backgroundColor !== 'transparent' ? overlay.backgroundColor : undefined,
                    borderRadius: overlay.borderRadius,
                    padding: overlay.backgroundColor !== 'transparent' ? '4px 8px' : undefined,
                    textShadow: '0 2px 8px rgba(0,0,0,0.6)',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                  }}
                >
                  {overlay.content}
                </div>
              )}

              {/* Selection handles */}
              {isSelected && (
                <>
                  <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-orange-400 rounded-full border border-white" />
                  <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-400 rounded-full border border-white" />
                  <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-orange-400 rounded-full border border-white" />
                  <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-orange-400 rounded-full border border-white" />
                </>
              )}
            </div>
          );
        })}

        {/* Empty state hint */}
        {overlays.length === 0 && (
          <div className="absolute inset-0 flex items-end justify-center pb-8 pointer-events-none">
            <div className="bg-black/50 backdrop-blur-sm text-white text-xs px-4 py-2 rounded-full flex items-center gap-2">
              <i className="ri-add-circle-line text-orange-400" />
              Add text, CTA, or logo from the left panel
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
